import axios from "axios";

/**
 * Backend resolution strategy
 * --------------------------
 * Always prefer the **current page origin** so requests stay same-origin across
 * environments (preview, emergent.host, custom domain like gomesdigitallab.com).
 * Same-origin requests:
 *   - Avoid CORS / SameSite cookie blocks
 *   - Carry credentials cleanly
 *   - Make the build environment-agnostic
 *
 * `REACT_APP_BACKEND_URL` is kept only as a safety fallback (e.g. SSR or
 * non-browser test runners where `window` is undefined).
 */
const BACKEND_URL =
  typeof window !== "undefined" && window.location?.origin
    ? window.location.origin
    : process.env.REACT_APP_BACKEND_URL;

export { BACKEND_URL };
export const API = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("gdl_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export default api;

export function formatApiError(err) {
  const detail = err?.response?.data?.detail;
  if (detail == null) return err?.message || "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e?.msg ? e.msg : JSON.stringify(e))).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}

/**
 * mediaUrl — used by upload flows. ALWAYS returns the relative `/api/media/...`
 * path so the value persisted to MongoDB is environment-agnostic. External
 * URLs (http*) are passed through unchanged.
 *
 * Why this matters: if we stored an absolute URL containing the *current*
 * REACT_APP_BACKEND_URL at upload time, every record would be permanently
 * pinned to whatever domain happened to be active during the upload (e.g.
 * the preview) — and break across environments.
 */
export function mediaUrl(pathOrUrl) {
  if (!pathOrUrl) return "";
  if (pathOrUrl.startsWith("http")) {
    // If someone uploaded against a different backend, strip the host so we
    // store only the canonical /api path. Otherwise (a true external URL like
    // a CDN) leave it as-is.
    try {
      const u = new URL(pathOrUrl);
      if (u.pathname.startsWith("/api/")) return u.pathname;
      return pathOrUrl;
    } catch {
      return pathOrUrl;
    }
  }
  return pathOrUrl;
}

/**
 * assetUrl — used at RENDER time on `<img src>`. Resolves a stored value to a
 * fully-qualified URL using the *current* environment's REACT_APP_BACKEND_URL.
 * Accepts:
 *   - relative `/api/media/file/...` (the new canonical form) → prefixed
 *   - external `http(s)://...` URLs → returned unchanged
 *   - empty/null → "" (so <img src=""> doesn't 404 a wrong path)
 *   - legacy absolute URLs that contain `/api/media/...` from a different
 *     environment (e.g. preview) → rewritten to current backend host
 */
export function assetUrl(value) {
  if (!value) return "";
  if (value.startsWith("/api/")) return `${BACKEND_URL}${value}`;
  if (value.startsWith("http")) {
    try {
      const u = new URL(value);
      if (u.pathname.startsWith("/api/media/")) return `${BACKEND_URL}${u.pathname}`;
      return value;
    } catch {
      return value;
    }
  }
  return value;
}
